/**
 * CollectionItemStatus class
 * Represents the ownership status flags for a collection item.
 */
class CollectionItemStatus {
    constructor(own = false, wishlist = false, trade = false, wantintrade = false, wanttobuy = false) {
        this.own = own;
        this.wishlist = wishlist;
        this.trade = trade;
        this.wantintrade = wantintrade;
        this.wanttobuy = wanttobuy;
    }

    /**
     * Check if any status flag is set
     * @returns {boolean}
     */
    hasAnyStatus() {
        return this.own || this.wishlist || this.trade || this.wantintrade || this.wanttobuy;
    }

    /**
     * Create status from XML attribute parsing
     * @param {Element} statusElement - XML status element
     * @returns {CollectionItemStatus}
     */
    static fromXml(statusElement) {
        if (!statusElement) {
            return new CollectionItemStatus();
        }
        return new CollectionItemStatus(
            statusElement.getAttribute('own') === '1',
            statusElement.getAttribute('wishlist') === '1',
            statusElement.getAttribute('trade') === '1',
            statusElement.getAttribute('want') === '1',
            statusElement.getAttribute('wanttobuy') === '1'
        );
    }
}

/**
 * CollectionItem class
 * 
 * Represents a game in a user's BGG collection.
 * Used in the trade matching feature and seller utility.
 */
class CollectionItem {
    constructor(id, name, status, versionInfo = {}) {
        this.id = id;
        this.name = name;
        this.status = status instanceof CollectionItemStatus 
            ? status 
            : new CollectionItemStatus();
        // Version info for seller utility
        this.versionId = versionInfo.versionId || null;
        this.versionName = versionInfo.versionName || '';
        this.imageId = versionInfo.imageId || null;
        this.thumbnail = versionInfo.thumbnail || null;
        this.image = versionInfo.image || null;
        this.yearPublished = versionInfo.yearPublished || null;
    }

    /**
     * Check if this item matches the given filters
     * @param {Object} filters - Filter flags
     * @returns {boolean}
     */
    matchesFilters(filters) {
        return (filters.collection && this.status.own) ||
               (filters.wantlist && this.status.wishlist) ||
               (filters.tradelist && this.status.trade) ||
               (filters.wantintrade && this.status.wantintrade) ||
               (filters.wanttobuy && this.status.wanttobuy);
    }

    /**
     * Create a CollectionItem from XML element
     * @param {Element} itemElement - XML item element
     * @returns {CollectionItem}
     */
    static fromXml(itemElement) {
        const id = itemElement.getAttribute('objectid');
        const nameElement = itemElement.getElementsByTagName('name')[0];
        const name = nameElement ? nameElement.textContent : '';
        const statusElement = itemElement.getElementsByTagName('status')[0];
        
        return new CollectionItem(
            id,
            name,
            CollectionItemStatus.fromXml(statusElement)
        );
    }

    /**
     * Create a CollectionItem from XML element with version info
     * Used for seller utility to get version details for VFM links
     * @param {Element} itemElement - XML item element
     * @returns {CollectionItem}
     */
    static fromXmlWithVersion(itemElement) {
        const id = itemElement.getAttribute('objectid');
        const nameElement = itemElement.getElementsByTagName('name')[0];
        const name = nameElement ? nameElement.textContent : '';
        const statusElement = itemElement.getElementsByTagName('status')[0];
        
        // Extract version info
        const versionElement = itemElement.getElementsByTagName('version')[0];
        const versionInfo = {};
        
        if (versionElement) {
            const versionItem = versionElement.getElementsByTagName('item')[0];
            if (versionItem) {
                versionInfo.versionId = versionItem.getAttribute('id');
                
                // Get version name
                const versionNameElement = versionItem.getElementsByTagName('name')[0];
                if (versionNameElement) {
                    versionInfo.versionName = versionNameElement.getAttribute('value') || versionNameElement.textContent;
                }
                
                // Get image info from version
                const imageElement = versionItem.getElementsByTagName('image')[0];
                if (imageElement) {
                    versionInfo.imageId = imageElement.getAttribute('id');
                }
            }
        }
        
        // Get thumbnail and image from main item
        const thumbnailElement = itemElement.getElementsByTagName('thumbnail')[0];
        const imageElement = itemElement.getElementsByTagName('image')[0];
        const yearElement = itemElement.getElementsByTagName('yearpublished')[0];
        
        versionInfo.thumbnail = thumbnailElement ? thumbnailElement.textContent : null;
        versionInfo.image = imageElement ? imageElement.textContent : null;
        versionInfo.yearPublished = yearElement ? yearElement.textContent : null;
        
        return new CollectionItem(
            id,
            name,
            CollectionItemStatus.fromXml(statusElement),
            versionInfo
        );
    }
}

export { CollectionItemStatus };
export default CollectionItem;